export const LIST_LIMIT = 20;
export const LIST_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];

export const STATUS_CHIPS = [
  { value: "all", label: "All" },
  { value: "vesting", label: "Vesting" },
  { value: "complete", label: "Complete" },
];

export const SEQUENCE_FALLBACK =
  "Each user's total reward is split across 4 rounds by plan % (example 10 / 15 / 25 / leftover). Last round pays whatever is left so the total is 100%. Rounds must be released in order. Funds go to the staking wallet only. The same user is not credited twice for the same round.";

export const SEQUENCE_RULE_FALLBACK =
  "Rounds must be released in order. If round 1 is not released, round 2 cannot be released. If round 2 is not released, round 3 cannot be released.";

export const releaseHintFallback = (round, percentage, isLast = false) => {
  const pct = percentage != null && percentage !== "" ? Number(percentage) : null;
  const pctText = Number.isFinite(pct) ? `${pct}%` : "this round's share";
  if (Number(round) === 1) {
    return `Releases ${pctText} of this user's total reward to the staking wallet. One credit per user.`;
  }
  if (isLast) {
    return `Releases the remaining ${pctText} (leftover) of this user's total reward. Skipped if previous rounds are pending. One credit per user.`;
  }
  return `Releases ${pctText} of this user's total reward. Skipped if the previous round is still pending. One credit per user.`;
};

export const asNum = (v) => {
  if (v == null || v === "") return 0;
  if (typeof v === "object" && v.$numberDecimal != null) {
    const n = Number(v.$numberDecimal);
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const formatMoney = (v, currency, { min = 2, max = 8 } = {}) => {
  if (v == null || v === "") return currency ? `— ${currency}` : "—";
  const n = asNum(v);
  const formatted = n.toLocaleString("en-IN", {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  });
  return currency ? `${formatted} ${currency}` : formatted;
};

export const userDisplayName = (user) => {
  if (!user) return "—";
  const parts = [user.first_name, user.last_name].filter(Boolean);
  if (parts.length) return parts.join(" ");
  return user.email || user.uuid || "—";
};

export const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return String(iso);
  }
};

export const releasedByLabel = (value) => {
  const s = String(value || "").toLowerCase();
  if (s === "admin_manual") return "Admin (manual)";
  if (s === "cron") return "Cron";
  if (s === "instant_approve") return "Instant approve";
  return value || "—";
};

export const remainingRoundChip = (row) => {
  const round = row?.round ?? row?.Round;
  const amount = row?.amount;
  const currency = row?.currency || "USDT";
  const pct = row?.percentage;
  return `R${round}${pct != null ? ` ${pct}%` : ""} · ${formatMoney(amount, currency, { min: 0, max: 8 })}`;
};

export const isUnauthorized = (res) => {
  const status = Number(res?.httpStatus);
  if (status === 401) return true;
  const msg = String(res?.message || "").toLowerCase();
  return msg.includes("unauthorized") || msg.includes("jwt") || msg.includes("token is expired");
};

export const isRoundReleased = (released) =>
  released === true || released === "true" || released === 1 || released === "1";

export const roundMoney = (n) => {
  const x = asNum(n);
  return Math.round((x + Number.EPSILON) * 1e8) / 1e8;
};

export const earningAccordionTitle = (vesting) => {
  const level = vesting?.level != null && vesting.level !== "" ? `L${vesting.level}` : "L—";
  const ccy = vesting?.currency || "USDT";
  const amount = formatMoney(vesting?.reward_amount ?? vesting?.amount, ccy, {
    min: 0,
    max: 8,
  });
  return `${level} · ${amount}`;
};

export const assignedRoundOf = (vesting, indexAmongCurrency = 0) => {
  if (vesting?.assigned_round != null && vesting.assigned_round !== "") {
    const n = Number(vesting.assigned_round);
    if (Number.isFinite(n)) return n;
  }
  return indexAmongCurrency < 3 ? indexAmongCurrency + 1 : 4;
};

export const isReleaseJobRunning = (payload) => {
  const d =
    payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)
      ? payload.data
      : payload || {};
  if (d.running === true) return true;
  if (d.running === false) return false;
  const status = String(d.status || d.result?.status || "").toLowerCase();
  return status === "processing";
};

const statusFromTotals = (paid, pending, due) => {
  if (due <= 0 && paid <= 0) return "empty";
  if (paid > 0 && pending <= 0) return "paid";
  if (paid > 0 && pending > 0) return "partial";
  return "pending";
};

export const normalizeRoundTotal = (row) => {
  const totalDue = asNum(row?.total_due);
  const paid = asNum(row?.paid);
  const pending = row?.pending != null ? asNum(row.pending) : roundMoney(totalDue - paid);
  const status = String(row?.status || statusFromTotals(paid, pending, totalDue)).toLowerCase();
  return {
    round: Number(row?.round),
    from_month: row?.from_month ?? row?.fromMonth ?? null,
    to_month: row?.to_month ?? row?.toMonth ?? null,
    total_due: totalDue,
    paid,
    pending,
    earning_count: row?.earning_count != null ? Number(row.earning_count) : null,
    percentage: row?.percentage != null && row.percentage !== "" ? asNum(row.percentage) : null,
    status,
    wallet_type: paid > 0 ? row?.wallet_type || "staking" : row?.wallet_type || null,
    currency: row?.currency || "USDT",
    released: row?.released,
  };
};

const groupNormalizedTotals = (rows) => {
  const byCcy = new Map();
  rows.forEach((row) => {
    if (!Number.isFinite(row.round)) return;
    const ccy = row.currency || "USDT";
    if (!byCcy.has(ccy)) byCcy.set(ccy, []);
    byCcy.get(ccy).push(row);
  });
  return [...byCcy.entries()]
    .sort(([a], [b]) => String(a).localeCompare(String(b)))
    .map(([currency, list]) => ({
      currency,
      rows: list.sort((a, b) => a.round - b.round),
    }));
};

const DEFAULT_PLAN_ROUNDS = [
  { round: 1, percentage: 10, from_month: 1, to_month: 3 },
  { round: 2, percentage: 15, from_month: 4, to_month: 6 },
  { round: 3, percentage: 25, from_month: 7, to_month: 12 },
  { round: 4, percentage: 50, from_month: 13, to_month: 24 },
];

/** Prefer API round_totals. Fallback: % of this user's total reward; last round leftover. */
export const roundTotalsFromPayload = (data) => {
  const apiRows = Array.isArray(data?.round_totals) ? data.round_totals : [];
  if (apiRows.length) return groupNormalizedTotals(apiRows.map(normalizeRoundTotal));

  const vestings = Array.isArray(data?.vestings) ? data.vestings : [];
  const history = Array.isArray(data?.release_history) ? data.release_history : [];
  const lastReleased = Number(data?.summary?.last_released_round) || 0;

  const byCcy = new Map();
  vestings.forEach((v) => {
    const ccy = v?.currency || "USDT";
    if (!byCcy.has(ccy)) byCcy.set(ccy, []);
    byCcy.get(ccy).push(v);
  });

  const rows = [];
  byCcy.forEach((list, ccy) => {
    const total = roundMoney(
      list.reduce((sum, v) => sum + asNum(v?.reward_amount ?? v?.amount), 0)
    );
    const template =
      Array.isArray(list[0]?.rounds) && list[0].rounds.length
        ? [...list[0].rounds].sort((a, b) => Number(a.round) - Number(b.round))
        : DEFAULT_PLAN_ROUNDS;
    const lastIdx = template.length - 1;
    let allocated = 0;
    template.forEach((pr, i) => {
      const round = Number(pr.round) || i + 1;
      const pct = asNum(pr.percentage);
      const isLast = i === lastIdx;
      const due = isLast ? roundMoney(total - allocated) : roundMoney((total * pct) / 100);
      if (!isLast) allocated = roundMoney(allocated + due);
      const hist = history.find(
        (h) => Number(h.round) === round && (h.currency || ccy) === ccy
      );
      const released =
        lastReleased >= round ||
        isRoundReleased(pr.released) ||
        Boolean(hist);
      const paid = released ? (hist != null ? asNum(hist.amount) : due) : 0;
      rows.push({
        round,
        from_month: pr.from_month ?? pr.fromMonth ?? null,
        to_month: pr.to_month ?? pr.toMonth ?? null,
        percentage: pct || null,
        total_due: due,
        paid,
        pending: roundMoney(due - paid),
        earning_count: list.length,
        status: statusFromTotals(paid, roundMoney(due - paid), due),
        wallet_type: paid > 0 ? hist?.wallet_type || pr.wallet_type || "staking" : null,
        currency: ccy,
      });
    });
  });

  return groupNormalizedTotals(rows);
};

/**
 * Flatten vestings[].rounds[] and group by currency + round.
 * Never add different currencies together.
 */
export const groupVestingsByRound = (vestings) => {
  const byCurrency = new Map();

  (Array.isArray(vestings) ? vestings : []).forEach((vesting) => {
    const vestingCcy = vesting?.currency || "";
    (Array.isArray(vesting?.rounds) ? vesting.rounds : []).forEach((r) => {
      const currency = r?.currency || vestingCcy || "USDT";
      const round = Number(r?.round);
      if (!Number.isFinite(round)) return;

      if (!byCurrency.has(currency)) byCurrency.set(currency, new Map());
      const byRound = byCurrency.get(currency);
      if (!byRound.has(round)) {
        byRound.set(round, {
          round,
          from_month: r?.from_month ?? null,
          to_month: r?.to_month ?? null,
          percentage: r?.percentage ?? null,
          currency,
          totalDue: 0,
          paid: 0,
          wallet_type: r?.wallet_type || "staking",
        });
      }
      const row = byRound.get(round);
      const amount = asNum(r?.amount);
      row.totalDue = roundMoney(row.totalDue + amount);
      if (isRoundReleased(r?.released)) {
        row.paid = roundMoney(row.paid + amount);
        if (r?.wallet_type) row.wallet_type = r.wallet_type;
      }
      if (row.from_month == null && r?.from_month != null) row.from_month = r.from_month;
      if (row.to_month == null && r?.to_month != null) row.to_month = r.to_month;
      if (row.percentage == null && r?.percentage != null) row.percentage = r.percentage;
    });
  });

  return [...byCurrency.entries()]
    .sort(([a], [b]) => String(a).localeCompare(String(b)))
    .map(([currency, byRound]) => ({
      currency,
      rows: [...byRound.values()]
        .map((row) => {
          const pending = roundMoney(row.totalDue - row.paid);
          let status = "pending";
          if (row.paid > 0 && pending <= 0) status = "paid";
          else if (row.paid > 0 && pending > 0) status = "partial";
          return {
            ...row,
            pending,
            status,
            wallet:
              status === "paid" || status === "partial" ? row.wallet_type || "staking" : "",
          };
        })
        .sort((a, b) => a.round - b.round),
    }));
};

export const copyText = async (text) => {
  const value = String(text || "");
  if (!value) return false;
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
};
